/**
 * The booth station, the desk: who's at it, and the operator's other hands — undo, reassign, cover, someone else, place on (split out of BoothStation.tsx
 * by interaction — James, Oct 21, item 5; no behavior change).
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { formatTime } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { useLoad } from '../admin/shared.js';
import { type Found, type Shift, first, hours } from './BoothShared.js';

export interface Desk {
  personId: string;
  name: string;
  since: string;
}
export interface Standby {
  id: string;
  personId: string;
  name: string;
  since: string;
  note: string | null;
}
export interface BoothDeskState {
  operator: string;
  setOperator: (v: string) => void;
  desk: Desk | null;
  deskOpen: boolean;
  setDeskOpen: (v: boolean) => void;
  onNow: { name: string; since: string; until: string | null }[];
  standby: Standby[];
  /** Whose name every action takes: who's at the desk, else the name typed. */
  opName: string;
  loadDesk: () => void;
  stillAsk: boolean;
  setStillAsk: (v: boolean) => void;
  lastTouch: { current: number };
}

/**
 * **Who's at the desk** (7f, F3): signed in once, by name, kept on the station; who has sat here
 * today; standby; and "Still Linda?" after ten minutes untouched.
 */
export function useBoothDesk(): BoothDeskState {
  // 7b: the name typed at the desk (until Who's at the desk lands), and the open hand.
  const [operator, setOperator] = useState('');
  // 7f: who's at the desk — signed in once, by name; actions take it from here.
  // Kept on the station too (ruling 1): a reload with no network still knows who is at the desk.
  const [desk, setDesk] = useState<{ personId: string; name: string; since: string } | null>(() => {
    try {
      const kept = localStorage.getItem('booth.desk');
      return kept ? (JSON.parse(kept) as { personId: string; name: string; since: string }) : null;
    } catch {
      return null;
    }
  });
  const [deskOpen, setDeskOpen] = useState(false);
  /** On at this booth (the rail): who has sat at this station today. */
  const [onNow, setOnNow] = useState<{ name: string; since: string; until: string | null }[]>([]);
  const [standby, setStandby] = useState<
    { id: string; personId: string; name: string; since: string; note: string | null }[]
  >([]);
  const opName = desk?.name ?? operator;

  const loadDesk = useCallback((): void => {
    api<{
      desk: { personId: string; name: string; since: string } | null;
      today?: { name: string; since: string; until: string | null }[];
    }>('GET', '/volunteers/booth/desk')
      .then((r) => {
        setDesk(r.desk);
        setOnNow(r.today ?? []);
        try {
          if (r.desk) localStorage.setItem('booth.desk', JSON.stringify(r.desk));
          else localStorage.removeItem('booth.desk');
        } catch {
          /* a private window keeps nothing; the server still knows */
        }
      })
      .catch(() => undefined);
    api<{
      rows: { id: string; personId: string; name: string; since: string; note: string | null }[];
    }>('GET', '/volunteers/booth/standby')
      .then((r) => {
        setStandby(r.rows);
      })
      .catch(() => undefined);
  }, []);
  useEffect(loadDesk, [loadDesk]);

  // "Still Linda?" (ON11 item 1): ten minutes with nothing touched, the station asks before the
  // next thing — yes, or somebody else takes the desk.
  const lastTouch = useRef(Date.now());
  const [stillAsk, setStillAsk] = useState(false);
  useEffect(() => {
    const touched = (): void => {
      lastTouch.current = Date.now();
    };
    window.addEventListener('pointerdown', touched);
    window.addEventListener('keydown', touched);
    const t = setInterval(() => {
      if (desk && Date.now() - lastTouch.current > 10 * 60_000) setStillAsk(true);
    }, 30_000);
    return () => {
      window.removeEventListener('pointerdown', touched);
      window.removeEventListener('keydown', touched);
      clearInterval(t);
    };
  }, [desk]);

  return {
    operator,
    setOperator,
    desk,
    deskOpen,
    setDeskOpen,
    onNow,
    standby,
    opName,
    loadDesk,
    stillAsk,
    setStillAsk,
    lastTouch,
  };
}

export function DeskSignIn({
  taking,
  station,
  onSignedIn,
  onCancel,
}: {
  taking: string | null;
  station: string;
  onSignedIn: (says: string) => void;
  onCancel: (() => void) | null;
}): ReactNode {
  const roster = useLoad<{
    shift: string | null;
    rows: { personId: string; name: string; says: string }[];
  }>('/volunteers/booth/desk/roster');
  const sitting = useLoad<{ sitting: { name: string; since: string; checkIns: number } | null }>(
    taking ? '/volunteers/booth/desk/sitting' : null,
  );
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Found[]>([]);
  const [other, setOther] = useState(false);
  const [chosen, setChosen] = useState<{ personId: string | null; name: string } | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) {
      setRows([]);
      return;
    }
    const t = setTimeout(() => {
      api<{ rows: Found[] }>('GET', `/volunteers/booth/find?q=${encodeURIComponent(text)}`)
        .then((r) => {
          setRows(r.rows);
        })
        .catch(() => {
          setRows([]);
        });
    }, 180);
    return () => {
      clearTimeout(t);
    };
  }, [q]);
  const start = (): void => {
    if (!chosen) return;
    setBusy(true);
    setRefusal(null);
    api<{ says: string }>(
      'POST',
      '/volunteers/booth/desk',
      chosen.personId ? { personId: chosen.personId } : { name: chosen.name },
    )
      .then((r) => {
        onSignedIn(r.says);
      })
      .catch((e: unknown) => {
        setRefusal(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const inWords = (says: string): string =>
    says.startsWith('checked in ')
      ? `booth operator · checked in ${formatTime(says.slice('checked in '.length))}`
      : `booth operator · ${says}`;
  const s = sitting.data?.sitting;
  return (
    <>
      {taking ? (
        <section className="booth__card" data-testid="booth-desk-signin">
          <div className="booth__cardhead">
            {chosen
              ? `${chosen.name.toUpperCase()} TAKES ${station.toUpperCase()}`
              : `WHO TAKES ${station.toUpperCase()}?`}
            <span className="booth__cardsub">{taking} signs out</span>
          </div>
          {chosen ? (
            <div className="booth__row">
              <span className="booth__rowa">
                <b>{chosen.name}</b>
                <span className="booth__rowb">their name, picked here</span>
              </span>
              <span className="booth__chip booth__chip--ok">NEXT</span>
            </div>
          ) : null}
          <div className="booth__row">
            <span className="booth__rowa">
              <b>{taking}</b>
              <span className="booth__rowb">
                {s
                  ? `${formatTime(s.since)} – now · ${String(s.checkIns)} check-in${s.checkIns === 1 ? '' : 's'}`
                  : 'at the desk now'}
              </span>
            </span>
            <span className="booth__rowc">Everything they did stays in their name.</span>
            <span className="booth__chip">SIGNED OUT</span>
          </div>
          <div className="booth__cardfoot">
            Left untouched for 10 minutes, the station asks “Still {first(taking)}?” before the next
            check-in.
          </div>
        </section>
      ) : null}
      <section
        className="booth__card"
        data-testid={taking ? 'booth-desk-roster' : 'booth-desk-signin'}
      >
        <div className="booth__cardhead">
          ON THE BOOTH SHIFT NOW
          <span className="booth__cardsub">
            {roster.data?.shift ? `${roster.data.shift} · from the roster` : 'from the roster'}
          </span>
        </div>
        {(roster.data?.rows ?? []).map((r) => (
          <div className="booth__row" key={r.personId}>
            <span className="booth__rowa">
              <b>{r.name}</b>
              <span className="booth__rowb">{inWords(r.says)}</span>
            </span>
            {chosen?.personId === r.personId ? (
              <span className="booth__chip booth__chip--ok">THAT’S ME</span>
            ) : (
              <button
                type="button"
                className="booth__act"
                onClick={() => {
                  setChosen({ personId: r.personId, name: r.name });
                }}
              >
                That’s me
              </button>
            )}
          </div>
        ))}
        {roster.data?.rows.length === 0 ? (
          <div className="booth__row">
            <span className="booth__rowa">
              <b>Nobody on a booth shift today</b>
              <span className="booth__rowb">find your name below, or type it</span>
            </span>
          </div>
        ) : null}
        <div className="booth__row">
          <span className="booth__rowa">
            <b>Someone not on the list</b>
          </span>
          <span className="booth__rowc">
            Find your own name — anyone, a chair too — or type it if the festival does not know you
            yet.
          </span>
          <button
            type="button"
            className="booth__act"
            onClick={() => {
              setOther(true);
            }}
          >
            Find my name
          </button>
        </div>
        {other ? (
          <>
            <label className="booth__row">
              <span className="booth__k">YOUR NAME</span>
              <input
                className="booth__input"
                aria-label="Find your name"
                autoComplete="off"
                autoFocus
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setChosen(null);
                }}
              />
            </label>
            {rows.map((p) => (
              <div className="booth__row" key={p.personId}>
                <span className="booth__rowa">
                  <b>{p.name}</b>
                </span>
                <button
                  type="button"
                  className="booth__act"
                  onClick={() => {
                    setChosen({ personId: p.personId, name: p.name });
                  }}
                >
                  That’s me
                </button>
              </div>
            ))}
            {q.trim().split(/\s+/).length >= 2 ? (
              <div className="booth__row">
                <span className="booth__rowa">
                  <b>{q.trim()}</b>
                  <span className="booth__rowb">not in People yet — becomes a person</span>
                </span>
                <button
                  type="button"
                  className="booth__act"
                  onClick={() => {
                    setChosen({ personId: null, name: q.trim() });
                  }}
                >
                  Use this name
                </button>
              </div>
            ) : null}
          </>
        ) : null}
        <div className="booth__cardfoot">
          The station opened with its code. Whoever is at the desk picks their own name; every
          check-in, undo, reassign and issue after carries it.
        </div>
      </section>
      {refusal ? (
        <p className="booth__refusal" role="alert">
          {refusal}
        </p>
      ) : null}
      <div className="booth__bigrow">
        <button
          type="button"
          className="booth__big booth__big--navy"
          disabled={busy || !chosen}
          onClick={start}
        >
          {taking ? `Hand over to ${chosen ? first(chosen.name) : '…'}` : `Start at ${station}`}
          <span className="booth__bigsub">
            {chosen ? `as ${chosen.name}` : 'pick your name first'}
          </span>
        </button>
        {onCancel ? (
          <button type="button" className="booth__side-act" onClick={onCancel}>
            Not now
          </button>
        ) : null}
      </div>
    </>
  );
}

/** The name typed at the desk, until Who's at the desk (7f) fills it. */
export function OperatorName({
  value,
  onChange,
  desk,
}: {
  value: string;
  onChange: (v: string) => void;
  desk: string | null;
}): ReactNode {
  if (desk)
    return (
      <div className="booth__row">
        <span className="booth__k">YOUR NAME</span>
        <span>{desk} · at the desk</span>
      </div>
    );
  return (
    <label className="booth__row">
      <span className="booth__k">YOUR NAME</span>
      <input
        className="booth__input"
        aria-label="Your name"
        required
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </label>
  );
}

/**
 * Find another person (7b): who is actually working a signup, or whose shift is being covered.
 * Its own search, so the station's box keeps the person in front of the desk.
 */
export function OtherPerson({
  title,
  note,
  operator,
  onOperator,
  desk,
  exclude,
  shiftsOf = false,
  onPick,
  onPickShift,
  pickLabel,
}: {
  title: string;
  note: string;
  operator: string;
  onOperator: (v: string) => void;
  desk: string | null;
  exclude: string;
  shiftsOf?: boolean;
  onPick?: (p: Found) => void;
  onPickShift?: (p: Found, s: Found['today'][number]) => void;
  pickLabel: (p: Found) => string;
}): ReactNode {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Found[]>([]);
  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) {
      setRows([]);
      return;
    }
    const t = setTimeout(() => {
      api<{ rows: Found[] }>('GET', `/volunteers/booth/find?q=${encodeURIComponent(text)}`)
        .then((r) => {
          setRows(r.rows.filter((x) => x.personId !== exclude));
        })
        .catch(() => {
          setRows([]);
        });
    }, 180);
    return () => {
      clearTimeout(t);
    };
  }, [q, exclude]);
  return (
    <div className="booth__hand" data-testid="booth-other">
      <div className="booth__k">{title}</div>
      <p className="booth__fine">{note}</p>
      <OperatorName value={operator} onChange={onOperator} desk={desk} />
      <label className="booth__row">
        <span className="booth__k">FIND</span>
        <input
          className="booth__input"
          aria-label="Find the other person"
          autoComplete="off"
          autoFocus
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
          }}
        />
      </label>
      {rows.map((p) =>
        shiftsOf ? (
          p.today
            .filter((s) => s.state === 'expected' && s.signupId && !s.workedBy)
            .map((s) => (
              <div className="booth__row" key={`${p.personId}${s.shiftId}`}>
                <span>
                  {p.name} · {s.area} · {hours(s.startsAt, s.endsAt)}
                </span>
                <button
                  type="button"
                  className="booth__btn"
                  disabled={!operator.trim()}
                  onClick={() => onPickShift?.(p, s)}
                >
                  Cover {pickLabel(p)}’s shift
                </button>
              </div>
            ))
        ) : (
          <div className="booth__row" key={p.personId}>
            <span>{p.name}</span>
            <button
              type="button"
              className="booth__btn"
              disabled={!operator.trim()}
              onClick={() => onPick?.(p)}
            >
              {pickLabel(p)}
            </button>
          </div>
        ),
      )}
    </div>
  );
}

/**
 * **Undo a check-in** (`Booth.dc.html` fix, Rebuild 29 state 24): the desk's name, one line of
 * why, and — if they were checked in to the wrong shift — the right one on the same press. The
 * check-in is kept and marked undone; the tag they took stops checking anyone out.
 */
export function UndoOne({
  who,
  shift,
  choices,
  operator,
  onOperator,
  desk,
  why,
  onWhy,
  busy,
  onUndo,
  onBack,
}: {
  who: Found;
  shift: Found['today'][number] | null;
  choices: Shift[];
  operator: string;
  onOperator: (v: string) => void;
  desk: string | null;
  why: string;
  onWhy: (v: string) => void;
  busy: boolean;
  onUndo: (then: Shift | null) => void;
  onBack: () => void;
}): ReactNode {
  const [to, setTo] = useState('');
  const then = choices.find((x) => x.shiftId === to) ?? null;
  return (
    <form
      className="booth__found"
      data-testid="booth-undo"
      onSubmit={(e) => {
        e.preventDefault();
        onUndo(then);
      }}
    >
      <section className="booth__card booth__card--gold">
        <div className="booth__cardhead">
          {who.name.toUpperCase()}
          <span className="booth__cardsub">
            {shift
              ? `${shift.inAt ? `checked in ${formatTime(shift.inAt)} · ` : ''}${shift.area} · ${hours(shift.startsAt, shift.endsAt)}`
              : ''}
          </span>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>Your name</b>
          </span>
          <span className="booth__rowc">
            <OperatorName value={operator} onChange={onOperator} desk={desk} />
          </span>
          <span className="booth__chip booth__chip--ok">NAMED</span>
        </div>
        <label className="booth__row">
          <span className="booth__rowa">
            <b>Why</b>
            <span className="booth__rowb">One line. Required.</span>
          </span>
          <input
            className="booth__input"
            aria-label="Why"
            required
            value={why}
            onChange={(e) => {
              onWhy(e.target.value);
            }}
          />
          <span className="booth__chip booth__chip--warn">REQUIRED</span>
        </label>
        <label className="booth__row">
          <span className="booth__rowa">
            <b>And check them in to</b>
            <span className="booth__rowb">if it was the wrong shift</span>
          </span>
          <select
            className="booth__input"
            aria-label="And check them in to"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
            }}
          >
            <option value="">— only undo —</option>
            {choices.map((x) => (
              <option key={x.shiftId} value={x.shiftId}>
                {x.area} · {hours(x.startsAt, x.endsAt)} · short {x.short}
              </option>
            ))}
          </select>
        </label>
        <div className="booth__cardfoot">
          The check-in is kept and marked undone, with who and why. The tag they took stops checking
          anyone out. The booth chair sees every undo on the admin station.
        </div>
      </section>
      <div className="booth__bigrow">
        <button
          type="submit"
          className="booth__big booth__big--red"
          data-testid="booth-big"
          disabled={busy || !operator.trim() || !why.trim()}
        >
          {then ? `Undo and check ${first(who.name)} in to ${then.area}` : 'Undo the check-in'}
        </button>
        <button type="button" className="booth__side-act" onClick={onBack}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * **Reassign · one verb** (`Booth.dc.html` reassign, Rebuild 29 state 14): from, to — the short
 * shifts first, the destination's own count — and who is told. Somebody in closes this segment at
 * the real time with the credit theirs; somebody not in yet has their signup moved. A full shift
 * is a proceed-anyway with a reason.
 */
export function ReassignOne({
  who,
  from,
  choices,
  operator,
  onOperator,
  desk,
  busy,
  onReassign,
  onBack,
}: {
  who: Found;
  from: Found['today'][number] | null;
  choices: Shift[];
  operator: string;
  onOperator: (v: string) => void;
  desk: string | null;
  busy: boolean;
  onReassign: (toShiftId: string, reason: string) => void;
  onBack: () => void;
}): ReactNode {
  const [to, setTo] = useState<Shift | null>(null);
  const [reason, setReason] = useState('');
  const w = first(who.name);
  const isIn = from?.state === 'in';
  const sorted = [...choices].sort((a, b) => b.short - a.short);
  const full = to ? to.short <= 0 : false;
  return (
    <div className="booth__found" data-testid="booth-reassign">
      <section className="booth__card">
        <div className="booth__cardhead">
          FROM
          <span className="booth__cardsub">{isIn ? 'checked in · mid-shift' : 'not in yet'}</span>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>{from ? `${from.area} · ${hours(from.startsAt, from.endsAt)}` : 'Their shift'}</b>
          </span>
          <span className="booth__rowc">
            {isIn
              ? `Closes this segment at ${formatTime(new Date())}, the real time. The credit for it stays with ${w}.`
              : `${w}’s signup moves, and ${w} is checked in where they go.`}
          </span>
          <span className="booth__chip booth__chip--ok">
            {isIn ? 'SEGMENT 1 CLOSES' : 'THE SIGNUP MOVES'}
          </span>
        </div>
      </section>
      <section className="booth__card booth__card--red">
        <div className="booth__cardhead">
          TO
          <span className="booth__cardsub">short first · the destination’s count</span>
        </div>
        {sorted.map((x) => {
          const tone = x.short >= 2 ? 'bad' : x.short === 1 ? 'warn' : 'in';
          return (
            <div
              className={`booth__row${to?.shiftId === x.shiftId ? ' booth__row--ok' : ''}`}
              key={x.shiftId}
            >
              <span className="booth__rowa">
                <b>
                  {x.area} · {hours(x.startsAt, x.endsAt)}
                </b>
                <span className="booth__rowb">
                  {x.title ? `${x.title} · ` : ''}
                  {x.short > 0 ? `short ${String(x.short)}` : 'full'}
                </span>
              </span>
              <span className="booth__rowc">
                {x.short > 0
                  ? ''
                  : 'Full — Proceed anyway adds a seat with your name and a reason.'}
              </span>
              <span className={`booth__chip booth__chip--${tone}`}>
                {x.short > 0 ? `SHORT ${String(x.short)}` : 'FULL'}
              </span>
              <button
                type="button"
                className="booth__act"
                onClick={() => {
                  setTo(x);
                }}
              >
                Choose
              </button>
            </div>
          );
        })}
        <div className="booth__cardfoot">
          The hard stops stop here too — the quiet flag, a minor without their adult, the area’s
          age. Everything else warns and proceeds.
        </div>
        {full ? (
          <label className="booth__row">
            <span className="booth__k">WHY</span>
            <input
              className="booth__input"
              aria-label="Why, if it is full"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
            <span className="booth__fine">Needed when the shift is full — proceed anyway.</span>
          </label>
        ) : null}
      </section>
      {to ? (
        <section className="booth__card booth__card--sky">
          <div className="booth__cardhead">
            WHO IS TOLD
            <span className="booth__cardsub">and what the roster says</span>
          </div>
          <div className="booth__row">
            <span className="booth__rowa">
              <b>The {from?.area ?? 'shift'}’s chairs</b>
              <span className="booth__rowb">
                “{who.name} moved from {from?.area ?? 'their shift'} to {to.area}”
              </span>
            </span>
            <span className="booth__chip booth__chip--info">BELL</span>
          </div>
          <div className="booth__row">
            <span className="booth__rowa">
              <b>The {to.area}’s chairs</b>
              <span className="booth__rowb">
                “{who.name} is on the way to {to.area} — reassigned from{' '}
                {from?.area ?? 'their shift'}”
              </span>
            </span>
            <span className="booth__chip booth__chip--info">BELL</span>
          </div>
          <div className="booth__row">
            <span className="booth__rowa">
              <b>{w}</b>
              <span className="booth__rowb">the guest screen says where to go</span>
            </span>
            <span className="booth__rowc">A new tag prints.</span>
            <span className="booth__chip booth__chip--ok">SCREEN</span>
          </div>
          <div className="booth__row">
            <span className="booth__rowa">
              <b>The roster</b>
              <span className="booth__rowb">
                “reassigned to {to.area} by {operator || 'you'}
                {reason.trim() ? ` — ${reason.trim()}` : ''}”
              </span>
            </span>
            <span className="booth__chip booth__chip--info">ROSTER</span>
          </div>
        </section>
      ) : null}
      <OperatorName value={operator} onChange={onOperator} desk={desk} />
      <div className="booth__bigrow">
        <button
          type="button"
          className="booth__big"
          data-testid="booth-big"
          disabled={busy || !to || !operator.trim() || (full && !reason.trim())}
          onClick={() => {
            if (to) onReassign(to.shiftId, reason);
          }}
        >
          {to ? `Reassign ${w} to ${to.area}` : 'Choose where'}
          <span className="booth__bigsub">a new tag prints</span>
        </button>
        <button type="button" className="booth__side-act" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}

/**
 * **Covering** (`Booth.dc.html` cover, Rebuild 29 state 13): "I'm here for Mike" — two taps, who
 * and which shift, then one press checks the cover in. A cover is a swap across households: the
 * credit goes to who works it.
 */
export function CoverFor({
  worker,
  operator,
  onOperator,
  desk,
  busy,
  onCover,
  onChoose,
  onBack,
}: {
  worker: Found;
  operator: string;
  onOperator: (v: string) => void;
  desk: string | null;
  busy: boolean;
  onCover: (covered: Found, s: Found['today'][number]) => void;
  onChoose: (covered: Found | null) => void;
  onBack: () => void;
}): ReactNode {
  const [chosen, setChosen] = useState<{ p: Found; s: Found['today'][number] } | null>(null);
  const w = first(worker.name);
  if (!chosen)
    return (
      <div className="booth__found" data-testid="booth-cover">
        <OtherPerson
          title={`${w.toUpperCase()} IS COVERING FOR…`}
          note="Find who they are covering, then the shift. Credit goes to them and their household, not the person covered."
          operator={operator}
          onOperator={onOperator}
          desk={desk}
          exclude={worker.personId}
          shiftsOf
          onPickShift={(p, s) => {
            setChosen({ p, s });
            onChoose(p);
          }}
          pickLabel={(p) => first(p.name)}
        />
        <div className="booth__bigrow">
          <button type="button" className="booth__side-act" onClick={onBack}>
            Not covering — back
          </button>
        </div>
      </div>
    );
  const { p, s } = chosen;
  const c = first(p.name);
  const mins = Math.round((Date.now() - new Date(s.startsAt).getTime()) / 60_000);
  const timing =
    Math.abs(mins) < 2
      ? `${w} is on time`
      : mins < 0
        ? `${w} is ${String(-mins)} minutes early`
        : `${w} is ${String(mins)} minutes late`;
  return (
    <div className="booth__found" data-testid="booth-cover">
      <section className="booth__card">
        <div className="booth__cardhead">
          COVERING
          <span className="booth__cardsub">two taps: who, which shift</span>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>For</b>
            <span className="booth__rowb">{p.name}</span>
          </span>
          <span className="booth__rowc">
            Found by name. A cover is a swap where the two aren’t in one household.
          </span>
          <span className="booth__chip booth__chip--ok">WHO</span>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>Shift</b>
            <span className="booth__rowb">
              {s.area} · {hours(s.startsAt, s.endsAt)}
            </span>
          </span>
          <span className="booth__rowc">
            {p.today.length === 1 ? `${c}’s only shift today.` : `One of ${c}’s shifts today.`}
          </span>
          <span className="booth__chip booth__chip--ok">SHIFT</span>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>What changes</b>
            <span className="booth__rowb">
              {c}’s row reads “covered by {worker.name}”
            </span>
          </span>
          <span className="booth__rowc">
            {w} is on the roster and on the chair’s app. Credit goes to {w} and their household, not{' '}
            {c}.
          </span>
          <span className="booth__chip booth__chip--info">ROSTER</span>
        </div>
        <div className="booth__cardfoot">
          The area’s chairs hear that {w} is covering {c}’s shift. {c} is not texted.
        </div>
      </section>
      <section className="booth__card booth__card--sky">
        <div className="booth__cardhead">
          EARLY OR LATE
          <span className="booth__cardsub">just times</span>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>{timing}</b>
          </span>
          <span className="booth__rowc">In now; the hours follow the hours rule.</span>
        </div>
      </section>
      <div className="booth__bigrow">
        <button
          type="button"
          className="booth__big"
          data-testid="booth-big"
          disabled={busy || !operator.trim()}
          onClick={() => {
            onCover(p, s);
          }}
        >
          Check {w} in for {c}
          <span className="booth__bigsub">
            {s.area} · {hours(s.startsAt, s.endsAt)}
          </span>
        </button>
        <button
          type="button"
          className="booth__side-act"
          onClick={() => {
            setChosen(null);
            onChoose(null);
          }}
        >
          Someone else
        </button>
        <button type="button" className="booth__side-act" onClick={onBack}>
          Not covering — back
        </button>
      </div>
    </div>
  );
}

/**
 * **Who is actually working?** (`Booth.dc.html` swapperson, Rebuild 29 state 12): the signup stays
 * and the person changes. The signer's household first — tap who is standing here — then anyone.
 * A minor is not offered an adult's shift here: the teen rule runs on their own card.
 */
export function WhoIsWorking({
  signer,
  shift,
  operator,
  onOperator,
  desk,
  busy,
  onPick,
  onBack,
}: {
  signer: Found;
  shift: Found['today'][number] | null;
  operator: string;
  onOperator: (v: string) => void;
  desk: string | null;
  busy: boolean;
  onPick: (p: Found) => void;
  onBack: () => void;
}): ReactNode {
  const [house, setHouse] = useState<{ name: string | null; members: Found[] } | null>(null);
  const [chosen, setChosen] = useState<Found | null>(null);
  useEffect(() => {
    api<{ name: string | null; members: Found[] }>(
      'GET',
      `/volunteers/booth/household-of?personId=${encodeURIComponent(signer.personId)}`,
    )
      .then(setHouse)
      .catch(() => {
        setHouse({ name: null, members: [] });
      });
  }, [signer.personId]);
  const where = shift ? `${shift.area} · ${hours(shift.startsAt, shift.endsAt)}` : 'the shift';
  const others = (house?.members ?? []).filter((m) => m.personId !== signer.personId);
  return (
    <div className="booth__found" data-testid="booth-swap">
      <div className="booth__banner" role="status">
        <b>THE SIGNUP STAYS, THE PERSON CHANGES</b>
        <span>
          {signer.name} signed up for {where}.{' '}
          {chosen ? `${chosen.name} is here to work it.` : 'Someone else is here to work it.'} One
          tap moves the shift; {first(signer.name)} is not a no-show; credit and hours go to who
          works it, and to their household.
        </span>
      </div>
      {others.length ? (
        <section className="booth__card">
          <div className="booth__cardhead">
            {(house?.name ? `The ${house.name} household` : 'The household').toUpperCase()}
            <span className="booth__cardsub">same address · tap who is here</span>
          </div>
          {others.map((m) => (
            <div
              className={`booth__row${chosen?.personId === m.personId ? ' booth__row--ok' : m.minor ? ' booth__row--warn' : ''}`}
              key={m.personId}
            >
              <span className="booth__rowa">
                <b>
                  {m.name}
                  {m.minor && typeof m.age === 'number' ? `, ${String(m.age)}` : ''}
                </b>
              </span>
              <span className="booth__rowc">
                {m.minor
                  ? 'A minor working an adult’s shift: the teen rule applies on their own card.'
                  : `${where} moves to ${first(m.name)}; ${first(signer.name)}’s row reads “worked by ${m.name}”.`}
              </span>
              {m.minor ? (
                <span className="booth__chip booth__chip--warn">RULES</span>
              ) : (
                <button
                  type="button"
                  className="booth__act"
                  onClick={() => {
                    setChosen(m);
                  }}
                >
                  It’s {first(m.name)}
                </button>
              )}
            </div>
          ))}
          <div className="booth__row">
            <span className="booth__rowa">
              <b>{signer.name}</b>
              <span className="booth__rowb">signed up · not here</span>
            </span>
            <span className="booth__rowc">
              Their row reads “worked by …”. Nothing else changes for them.
            </span>
            <span className="booth__chip booth__chip--in">SIGNED UP</span>
          </div>
        </section>
      ) : null}
      <OtherPerson
        title="NOT IN THE HOUSEHOLD · SEARCH ANYONE"
        note="A friend, a neighbor: the same swap, and the credit goes to their household — not the signer's."
        operator={operator}
        onOperator={onOperator}
        desk={desk}
        exclude={signer.personId}
        onPick={(p) => {
          setChosen(p);
        }}
        pickLabel={(w) => `It’s ${first(w.name)}`}
      />
      <div className="booth__bigrow">
        <button
          type="button"
          className="booth__big"
          data-testid="booth-big"
          disabled={busy || !chosen || !operator.trim()}
          onClick={() => {
            if (chosen) onPick(chosen);
          }}
        >
          {chosen ? `It’s ${first(chosen.name)} — move the shift to them` : 'Tap who is here'}
          <span className="booth__bigsub">then their waiver, then Check in</span>
        </button>
        <button type="button" className="booth__side-act" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}

export function PlaceOn({
  shifts,
  value,
  onChange,
}: {
  shifts: Shift[];
  value: string;
  onChange: (v: string) => void;
}): ReactNode {
  return (
    <label className="booth__row">
      <span className="booth__k">WHERE</span>
      <select
        className="booth__input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        {shifts.map((s) => (
          <option key={s.shiftId} value={s.shiftId}>
            {s.area} · {hours(s.startsAt, s.endsAt)}
            {s.short > 0 ? ` · short ${String(s.short)}` : ' · full'}
          </option>
        ))}
      </select>
      <span className="booth__fine">The shortest shifts first.</span>
    </label>
  );
}
